/* AutocompleteComponent component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Prop, h, Event, EventEmitter, Method, Watch, AttachInternals } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import AutocompleteTemplate from './cwc-autocomplete.template';
import { AbstractComponent } from '@cewe-designsystem/abstract';
import UtilEventKeyPolyfill from '@cewe-designsystem/util_event-key-polyfill';

/**
 * @slot icon - HTML element shown before the input as icon e.g. an img or svg
 * @slot reset-icon - HTML element used as caption for the button to reset the input e.g. an img or svg
 */
@Component({
  tag: 'cwc-component-autocomplete',
  styleUrl: './cwc-autocomplete.scss',
  shadow: true,
  formAssociated: true,
})
export class AutocompleteComponent {
  @AttachInternals() internals: ElementInternals;

  /** the input element for search query */
  searchInputElement: HTMLInputElement;

  /** the reset button for deleting the input value */
  resetBtnElement: HTMLInputElement;

  /** the suggestion list element */
  searchSuggestElement: HTMLElement;

  /**
   * Polyfill for key code detection.
   * @private
   */
  _eventKeyPolyfill = new UtilEventKeyPolyfill();

  /** the internal data */
  _data = [];

  /** initial/reset value of the element */
  _initialValue: string = '';

  /** timeout handle for search requests */
  _searchTimeoutHandle;

  /** array of search results */
  _searchResults = [];

  /** index of the selected suggest item */
  _searchResultSelected;

  /**
   * Event that is emitted if the user selects an item.
   */
  @Event({
    eventName: 'on:autocomplete:select',
    bubbles: true,
  })
  selectedEvent: EventEmitter;

  /**
   * Event similar to the input event of input fields.
   */
  @Event({
    eventName: 'on:autocomplete:input',
    bubbles: true,
  })
  inputEvent: EventEmitter;

  /** the open state of the autocomplete suggestion list */
  @Prop({ reflect: true, mutable: true })
  open: boolean;

  /** input field placeholder */
  @Prop({ reflect: false, mutable: false })
  placeholder: string;

  /** json object string of key-value pairs for autocompletes */
  @Prop({ reflect: false, mutable: false })
  data: string;

  /** url of data resource, %searchQuery% can be used as placeholder for injection of search */
  @Prop({ reflect: false, mutable: false })
  url: string;

  /** value of the selected value */
  @Prop({ reflect: true, mutable: true })
  value: string = '';

  /** min length of searchPhrase */
  @Prop({ reflect: false, mutable: false })
  minLength: number = 2;

  /** timeout after which the search will be done */
  @Prop({ reflect: false, mutable: false })
  inputTimeout: number = 500;

  /** is the given url a search service, so the searchQuery placeholder is used and the client side filtering is disabled */
  @Prop({ reflect: false, mutable: false, attribute: 'url-is-search' })
  isUrlService: boolean;

  /** key to use in data */
  @Prop({ reflect: false, mutable: false })
  dataKey: string = 'value';

  /** disable state */
  @Prop({ reflect: false, mutable: false })
  disabled: boolean = false;

  /** name of the element */
  @Prop({ reflect: true, mutable: false })
  name: string;

  /**
   * @inheritDoc
   */
  @Element()
  element: HTMLElement;

  /**
   * @inheritDoc
   */
  connectedCallback() {
    this._data = JSON.parse(this.element.getAttribute('data') || '[]');
    this._searchResults = this._data;
    this._initialValue = this.element.getAttribute('value') || '';
  }

  /**
   * @inheritDoc
   */
  componentDidLoad() {
    this.searchInputElement = this.getContent().querySelector('input');
    this.resetBtnElement = this.getContent().querySelector('.reset');
    this.searchSuggestElement = this.getContent().querySelector('cwc-component-autocomplete-suggestions');

    this.checkResetButtonVisibility();
  }

  /**
   * @inheritDoc
   */
  render() {
    return <AutocompleteTemplate controller={this} />;
  }

  /**
   * @inheritDoc
   */
  componentDidRender() {
    if (this.open) {
      this.searchSuggestElement.scrollTop = 0;
    }
  }

  /**
   * @inheritDoc
   */
  async formResetCallback() {
    this.onReset();
  }

  /**
   * @inheritDoc
   */
  async formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }

  /**
   *
   * @param {string} value
   * @private
   */
  @Method()
  async setValue(value: string) {
    this.value = value;
    this.searchInputElement.value = value;
    this.internals.setFormValue(value);
  }

  @Watch('value')
  checkResetButtonVisibility() {
    if (this.value.length > 0) {
      this.resetBtnElement.style.opacity = '1';
    } else {
      this.resetBtnElement.style.opacity = '0';
    }
  }

  @Watch('value')
  updateExternalValue(value) {
    this.searchInputElement.value = value;
    this.internals.setFormValue(value);
  }

  /**
   * resets the input value.
   * @param setFocus if true the input field gets focus after reset.
   * @private
   */
  onReset(setFocus: boolean = true) {
    this.setValue(this._initialValue);
    this._searchResultSelected?.classList.remove('selected');
    if (setFocus) {
      this.searchInputElement.focus();
    }
  }

  /**
   * @private
   */
  onInputfieldInput() {
    const searchValue = this.searchInputElement ? this.searchInputElement.value : '';

    // in IE11 this does not work, but there is no shadow DOM, so the input is reachable
    if (this?.internals['setFormValue']) {
      this.internals.setFormValue(searchValue);
    }

    this.setValue(searchValue);

    if (searchValue.length < this.minLength) {
      this.open = false;
      return;
    }

    window.clearTimeout(this._searchTimeoutHandle);
    this._searchTimeoutHandle = setTimeout(async () => {
      this._searchResults = await this._fetchSearchResults(searchValue);

      //show list only if there are results
      this.open = !!this._searchResults.length;

      // this.element.forceUpdate();
    }, this.inputTimeout);
  }

  /**
   * returns the suggestion data.
   * @param {string} searchValue
   * @private
   */
  async _fetchSearchResults(searchValue: string) {
    let results = this._data;

    if (this.url) {
      const response = await fetch(this.url.replace('%searchQuery%', searchValue), {});

      results = await response.json();
    }

    // filter data if local data is used or the url is a search service
    if (!this.url || !this.isUrlService) {
      results = results.filter((el) => {
        return el.value.match(new RegExp(searchValue, 'gi'));
      });
    }

    return results;
  }

  /**
   * sets the clicked element value as element value.
   * @param {Event} e
   * @private
   */
  onSearchItemClicked(e: Event) {
    this.open = false;
    this.setValue(e.target instanceof HTMLElement ? e.target.textContent : this._initialValue);
  }

  /**
   * blur event handler when user leaves input element.
   * @private
   */
  onBlurInput() {
    setTimeout(() => {
      this.open = false;
    }, 400); /* eslint-disable-line no-magic-numbers */
  }

  /**
   * handle key down inputs in input element.
   * @param {Event} e
   * @private
   */
  onInputKeyDown(e) {
    this._searchResultSelected =
      this.searchSuggestElement.querySelector('cwc-component-autocomplete-suggestions-item.selected') ||
      this.searchSuggestElement.querySelector('cwc-component-autocomplete-suggestions-item');

    if (this._eventKeyPolyfill.getKeyCode(e) === 'Enter') {
      // cancel Enter if pressed in form -> needed if there is no shadow root, then the form will be submitted
      e.preventDefault();
    }

    if (!this.open || !this._searchResultSelected) {
      return;
    }

    let newSelectedElement: HTMLElement;

    switch (this._eventKeyPolyfill.getKeyCode(e)) {
      case 'Enter':
        this.setValue(this._searchResultSelected.textContent || this._initialValue);
        this.open = false;
        return;

      case 'Escape':
        e.preventDefault();
        this.open = false;
        return;

      case 'ArrowUp':
        newSelectedElement = this._searchResultSelected.previousElementSibling || this._searchResultSelected;
        break;

      case 'ArrowDown':
        newSelectedElement = this._searchResultSelected.nextElementSibling || this._searchResultSelected;
        break;

      default:
        return;
    }

    e.preventDefault();

    this._searchResultSelected.classList.remove('selected');
    newSelectedElement.classList.add('selected');

    // scroll active element in viewable area
    this.searchSuggestElement.scrollTop = newSelectedElement.offsetTop;

    this._searchResultSelected = newSelectedElement;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(AutocompleteComponent.prototype, AbstractComponent.prototype);

export interface AutocompleteComponent extends AbstractComponent {}
